import { useEffect, useState } from "react";
import { Loader2, Sliders, RotateCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useBenchmarks, DEFAULT_BENCHMARKS, type Benchmarks } from "@/lib/useBenchmarks";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Lets the user override the labor / food / rent % targets that drive
 *  Operations Health and AI Recommendations. */
export default function BenchmarkSettingsDialog({ open, onOpenChange }: Props) {
  const { benchmarks, save } = useBenchmarks();
  const [draft, setDraft] = useState<Benchmarks>(benchmarks);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(benchmarks);
  }, [open, benchmarks]);

  const update = (key: keyof Benchmarks, value: string) => {
    const n = Math.max(0, Math.min(100, parseFloat(value) || 0));
    setDraft(d => ({ ...d, [key]: n }));
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await save(draft);
    setSaving(false);
    if (error) {
      toast.error("Could not save benchmarks. Please try again.");
      return;
    }
    toast.success("Benchmarks saved");
    onOpenChange(false);
  };

  const handleReset = () => setDraft(DEFAULT_BENCHMARKS);

  const fields: { key: keyof Benchmarks; label: string; help: string; defaultVal: number }[] = [
    { key: "laborPct", label: "Labor cost target", help: "Healthy restaurants typically run at 30% or less.", defaultVal: 30 },
    { key: "foodPct",  label: "Food cost target",  help: "Industry benchmark sits around 28–32%.",            defaultVal: 32 },
    { key: "rentPct",  label: "Rent target",       help: "Aim for rent under 10% of revenue.",                defaultVal: 10 },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-primary" />
            <DialogTitle>Benchmark settings</DialogTitle>
          </div>
          <DialogDescription>
            These targets power your Operations Health score and recommendations.
            Lower targets make the scoring stricter.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {fields.map(f => (
            <div key={f.key}>
              <Label className="text-sm font-medium text-foreground">
                {f.label} <span className="text-muted-foreground">(% of revenue)</span>
              </Label>
              <div className="mt-1.5 relative">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  inputMode="decimal"
                  value={draft[f.key]}
                  onChange={(e) => update(f.key, e.target.value)}
                  className="h-10 pr-9"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  %
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {f.help} Default: {f.defaultVal}%.
              </p>
            </div>
          ))}
        </div>
        <DialogFooter className="sm:justify-between gap-2 flex-col sm:flex-row mt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleReset}
            className="gap-1.5"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset to defaults
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}